import { cloneDeep } from 'lodash-es';
import moment from 'moment-timezone';

import { ILocalization } from '~/src/dash/api/console-api';

function createQuarterTimes(type: 'start' | 'end'): string[] {
  let quarterHours: string[];
  const times: string[] = [];

  const addStringToTimes = (i: number): void => {
    if (i === 0) {
      for (const minutes of quarterHours) {
        times.push('12:' + minutes);
      }
    } else if (i < 10) {
      for (const minutes of quarterHours) {
        times.push(`0${i}:` + minutes);
      }
    } else {
      for (const minutes of quarterHours) {
        times.push(`${i}:` + minutes);
      }
    }
  };

  if (type === 'start') {
    quarterHours = ['00', '15', '30', '45'];
    for (let i: number = 0; i < 12; i++) {
      addStringToTimes(i);
    }
  } else {
    quarterHours = ['59', '44', '29', '14'];
    for (let i: number = 11; i >= 0; i--) {
      addStringToTimes(i);
    }
  }
  return times;
}

export const quarterTimesStart: string[] = createQuarterTimes('start');

export const quarterTimesEnd: string[] = createQuarterTimes('end');

export const amPmOptions: string[] = ['AM', 'PM'];

export function toStartDate(date: moment.Moment | Date): moment.Moment {
  return moment(date).hour(0).minute(0).second(0).millisecond(0);
}

export function toEndDate(date: moment.Moment | Date): moment.Moment {
  return moment(date).hour(23).minute(59).second(59).millisecond(999);
}

export function formatDate(date: string | moment.Moment): string {
  return moment(date).utc().format('YYYY-MM-DDTHH:mm:ss');
}

export function formatTime(date: string | moment.Moment, timezone: string): string {
  return moment(date).tz(timezone).format('HH:mm:ss');
}

export function formatDateUsingTimezone(date: string | moment.Moment, timezone: string): string {
  return moment(date).tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
}

export function formatDateUsingTimezoneV2(date: string | moment.Moment, timezone: string): string {
  return moment(date).tz(timezone).format('YYYY-MM-DD HH:mm:ss');
}

export function formatDateStartDay(date: string | moment.Moment): string {
  return moment(date).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
}

export function formatDateEndDay(date: string | moment.Moment): string {
  return moment(date).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
}

export function formatYearMonthNameDate(date: string | moment.Moment): string {
  return moment(date).format('YYYY-MMMM-DD');
}

export function formatDateSmall(date: string | moment.Moment): string {
  return moment(date).utc().format('YYYY-MM-DD');
}

export function formatDayMonthName(date: string | moment.Moment): string {
  return moment(date).format('ddd, MMM D, YYYY');
}

export function formatHourDayMonth(date: string | moment.Moment): string {
  return moment(date).format('HH:mm ddd, MMM D');
}

export function formatDateWithOffsetToUTC(date: moment.Moment, timezone: string): string {
  return moment.tz(date.format('YYYY-MM-DDTHH:mm:ss'), timezone).format('YYYY-MM-DDTHH:mm:ssZ');
}

export function formatDateFromUTCToOffset(date: string, timezone: string): moment.Moment {
  return moment.utc(date).tz(timezone);
}

export function parseDate(date: string): moment.Moment {
  return moment.utc(date);
}

export function getDateWithOffset(date: string | moment.Moment, timezone: string): moment.Moment {
  return moment(date).tz(timezone);
}

export function getToday(timeZone: string): moment.Moment {
  return moment().tz(timeZone);
}

export function getStartDate(
  timeZone: string,
  offset: number,
  m: moment.unitOfTime.DurationConstructor,
): moment.Moment {
  return parseDate(new Date().toLocaleString('en-US', { timeZone })).subtract(offset, m);
}

export function getEndDate(timeZone: string): moment.Moment {
  return parseDate(new Date().toLocaleString('en-US', { timeZone }));
}

export function getFutureStartDate(timeZone: string): moment.Moment {
  return toStartDate(
    parseDate(new Date().toLocaleString('en-US', { timeZone })).tz(timeZone).add(1, 'day'),
  );
}

export function getFutureEndDate(
  timeZone: string,
  offset: number,
  m: moment.unitOfTime.DurationConstructor,
): moment.Moment {
  return toEndDate(
    parseDate(new Date().toLocaleString('en-US', { timeZone })).add(1, 'day').add(offset, m),
  );
}

export function hasCampaignEnded(endTime: moment.Moment): boolean {
  return Date.now() > endTime.unix() * 1000;
}

export function hasCampaignStarted(startTime: moment.Moment): boolean {
  return Date.now() > startTime.unix() * 1000;
}

export function formatUnixToTimeInTimezone(unixTimestamp: number, timezone: string): string {
  return moment.unix(unixTimestamp).tz(timezone).format('HH:mm');
}

export function formatUnixToTimeWithDateInTimezone(
  unixTimestamp: number,
  timezone: string,
): string {
  return moment.unix(unixTimestamp).tz(timezone).format('MM-DD HH:mm');
}

export function formatUnixToTimeExtendedInTimezone(
  unixTimestamp: number,
  timezone: string,
): string {
  return moment.unix(unixTimestamp).tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ');
}

// Date functions using localization
export function checkLocalizationDateFormat(localization: ILocalization | null): string {
  if (
    localization === null ||
    localization.DateFormat === '' ||
    localization.DateFormat === undefined
  ) {
    return 'MM/DD/YYYY HH:mm';
  } else {
    return localization.DateFormat;
  }
}

export function printDate(
  date: string | moment.Moment | undefined,
  localization: ILocalization | null,
): string {
  if (date === undefined) {
    return 'mm/dd/yyyy, --:-- --';
  }
  const setDateFormat = checkLocalizationDateFormat(localization);
  return moment(date).utc().format(setDateFormat);
}

// Remove the hours and minutes from the date format
export function formatWithoutHoursMins(localization: ILocalization | null) {
  const dateFormat = checkLocalizationDateFormat(localization);
  return dateFormat.slice(0, -6);
}

// Validate start and end date-times, to be used where DatetimeRangeComponent is used
export function validateDatetimes(
  start: moment.Moment | undefined,
  end: moment.Moment | undefined,
  minDate: moment.Moment | undefined,
): string[] {
  const errorMsgs = [];

  if (!start) {
    errorMsgs.push('Please provide start date');
  }
  if (!end) {
    errorMsgs.push('Please provide end date');
  }
  if (!start || !end) {
    return errorMsgs;
  }

  // Back-end api maxes out at 2038
  // and only allows calendar to have a range of 10 years ahead
  const maxDate = moment.min(end.clone().add(10, 'year'), moment([2037, 1, 1]));

  if (start.isAfter(end)) {
    errorMsgs.push('Start time must be before end time');
  }
  if (end.isAfter(maxDate)) {
    errorMsgs.push('End time exceeds future limit');
  }
  if (moment.isMoment(minDate) && start.isBefore(minDate)) {
    errorMsgs.push('Start time is too early');
  }
  return errorMsgs;
}

export function overrideTimezoneForDate(date: moment.Moment, timezone: string): moment.Moment {
  const dateWithoutTimezone = moment(date).format('YYYY-MM-DDTHH:mm:ss.SSS');
  return moment.tz(dateWithoutTimezone, timezone || 'UTC');
}

export type DateGroup = 'hour' | 'day' | 'week' | 'month';
export const DateGroupHours: DateGroup = 'hour';
export const DateGroupDays: DateGroup = 'day';
export const DateGroupWeeks: DateGroup = 'week';
export const DateGroupMonths: DateGroup = 'month';

// checks the day difference for the dates are matching and returns true if they date ranges match in date count
// ex:
// (Jan 1st - Jan 3rd) and (Feb 2nd - Feb 4th) have matching day diffs
// (Jan 1st - Jan 3r)d and (Feb 2nd - Feb 5th) don't have matching day diffs
export function areDateRangesMatchingByDay(
  startDate1: moment.Moment,
  endDate1: moment.Moment,
  startDate2: moment.Moment,
  endDate2: moment.Moment,
): boolean {
  // set start of to ignore time aspects
  const dateDiff1 = endDate1.clone().startOf('day').diff(startDate1, 'days');
  const dateDiff2 = endDate2.clone().startOf('day').diff(startDate2, 'days');
  return dateDiff1 === dateDiff2;
}

export const convertTimestamps = (
  data: Array<Record<string, any>>,
  dateKeys: string[],
  timeZone: string,
) => {
  const convertedData = cloneDeep(data);
  convertedData.forEach((item, i) => {
    dateKeys.forEach((key) => {
      const updatedValue = moment.utc(item[key]).tz(timeZone).format();
      convertedData[i] = { ...item, [key]: updatedValue };
    });
  });
  return convertedData;
};

export const isDateTimeBeforeFallBack = (currentDate: moment.Moment): boolean => {
  if (currentDate.month() < 10) {
    return true;
  } else if (currentDate.month() === 10) {
    // Fall back happens on the first Sunday of November;
    // date of month is less than day of week only until the first Sunday of the month
    if (currentDate.date() <= currentDate.day()) {
      return true;
      // only the first 2 hours on the day of the time change occur before the time change
    } else if (currentDate.day() === 0 && currentDate.date() <= 7 && currentDate.hour() < 2) {
      return true;
    }
  }
  return false;
};

export const isDateToday = (date: moment.Moment, timezone: string) => {
  const today = moment().tz(timezone);
  return today.isSame(date, 'day');
};

export const isDateTodayWithTimezone = (date: moment.Moment, timezone: string) => {
  return moment().tz(timezone).format('YYYY-MM-DD') === date.tz(timezone).format('YYYY-MM-DD');
};
